import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    Button,
    LinearProgress,
    Paper
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import MoneyOffRoundedIcon from '@mui/icons-material/MoneyOffRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DataGrid } from '@mui/x-data-grid';

import BiMetric from '../../components/biMetric';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import { csvDecimal, exportRowsToCsv } from '../../utils/csvExport';
import { formatInteger } from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import {
    buildSituacaoRows,
    buildSituacaoSummary,
    fetchSituacaoInadimplentes
} from './situacaoInadimplentesData';
import '../css/biAluguelAccess.css';
import '../css/biSituacaoInadimplentes.css';

const emptyRowsLabel = 'Nenhum inadimplente encontrado.';
const csvBaseName = 'situacao-inadimplentes';

const SituacaoInadimplentesBIGE = () => {
    const [inadimplentes, setInadimplentes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadInadimplentes = useCallback(async () => {
        setIsLoading(true);
        const situacao = await fetchSituacaoInadimplentes();
        setInadimplentes(situacao);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadInadimplentes();
    }, [loadInadimplentes]);

    const rows = useMemo(() => buildSituacaoRows(inadimplentes), [inadimplentes]);
    const summary = useMemo(() => buildSituacaoSummary(inadimplentes), [inadimplentes]);

    const columns = useMemo(() => [
        {
            field: 'cc',
            headerName: 'CC',
            minWidth: 140,
            flex: 0.6
        },
        {
            field: 'faturas',
            headerName: 'Faturas',
            type: 'number',
            width: 110,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => formatInteger(value)
        },
        {
            field: 'valorDevido',
            headerName: 'Valor Devido',
            type: 'number',
            minWidth: 150,
            flex: 0.6,
            valueFormatter: (value) => formatCurrency(value),
            csvFormatter: csvDecimal
        },
        {
            field: 'matricula',
            headerName: 'Matrícula',
            type: 'number',
            width: 120,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'cliente',
            headerName: 'Cliente',
            minWidth: 240,
            flex: 1.3
        },
        {
            field: 'contatoNome',
            headerName: 'Nome',
            minWidth: 180,
            flex: 0.9
        },
        {
            field: 'telefone',
            headerName: 'Telefone',
            minWidth: 150,
            flex: 0.6
        }
    ], []);

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-situacao-page'>
            <PageHeader>Situação Inadimplentes</PageHeader>

            <PageIntro eyebrow='BI Cobrança' title='Situação dos inadimplentes'>
                Unidades com faturas em aberto, valor devido e contato do cliente.
            </PageIntro>

            <section className='bi-aluguel-access-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-aluguel-access-metrics bi-ge-situacao-metrics'>
                    <BiMetric
                        className=''
                        icon={HomeWorkRoundedIcon}
                        label='Unidades'
                        value={formatInteger(summary.unidades)}
                        tone='blue'
                    />
                    <BiMetric
                        className=''
                        icon={ReceiptLongRoundedIcon}
                        label='Faturas'
                        value={formatInteger(summary.faturas)}
                        tone='navy'
                    />
                    <BiMetric
                        className=''
                        icon={MoneyOffRoundedIcon}
                        label='Valor devido'
                        value={formatCurrency(summary.valorDevido)}
                        tone='green'
                    />
                </div>

                <Paper className='bi-aluguel-access-panel bi-aluguel-access-table-panel'>
                    <div className='bi-aluguel-access-panel-heading'>
                        <div>
                            <p>Inadimplentes por unidade</p>
                            <h2>Situação das unidades</h2>
                        </div>

                        <div className='bi-ge-situacao-panel-actions'>
                            <Button
                                size='small'
                                variant='outlined'
                                startIcon={<DownloadRoundedIcon />}
                                disabled={isLoading || rows.length === 0}
                                onClick={() => exportRowsToCsv({ baseName: csvBaseName, columns, rows })}
                            >
                                Exportar CSV
                            </Button>
                            <WarningAmberRoundedIcon />
                        </div>
                    </div>

                    <div className='bi-aluguel-access-grid-shell bi-ge-situacao-grid-shell'>
                        <DataGrid
                            className='bi-aluguel-access-grid'
                            rows={rows}
                            columns={columns}
                            loading={isLoading}
                            disableRowSelectionOnClick
                            localeText={{ noRowsLabel: emptyRowsLabel }}
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 50
                                    }
                                },
                                sorting: {
                                    sortModel: [{ field: 'faturas', sort: 'desc' }]
                                }
                            }}
                        />
                    </div>
                </Paper>
            </section>
        </Box>
    );
};

export default SituacaoInadimplentesBIGE;
